/**
 * HealthScoreHero
 *
 * Hero card for the Executive Summary. Renders the composite Business Health
 * Score (0-100) with a circular progress ring and a per-driver breakdown.
 *
 * Palette compliance: NO red/green. Tiers map to the OnPoint BI palette:
 *   Excellent (80+):  Gold      #D4A017
 *   Strong    (60-79): Blue      #1B75BA
 *   Watch     (40-59): Orange    #F5841F
 *   Critical  (<40):   Purple    #8B5CF6
 *
 * Data source: GET /api/metrics/health-score/detail
 */
import { useEffect, useState } from "react";
import { Sparkles, ArrowUpRight } from "lucide-react";
import api from "../../../utils/api";
import MetricTooltip from "../MetricTooltip/MetricTooltip";
import { METRIC_TOOLTIPS } from "../MetricTooltip/metricCopy";
import CashRunwayChip from "./CashRunwayChip";

const TIER_COLORS = {
  excellent: { primary: "#D4A017", ring: "#D4A017", bgSoft: "#FEF3E6", ink: "#A16207", label: "Excellent" },
  strong:    { primary: "#1B75BA", ring: "#1B75BA", bgSoft: "#EFF6FF", ink: "#14588A", label: "Strong" },
  watch:     { primary: "#F5841F", ring: "#F5841F", bgSoft: "#FED7AA", ink: "#EA580C", label: "Watch" },
  critical:  { primary: "#8B5CF6", ring: "#8B5CF6", bgSoft: "#F4F6FB", ink: "#282560", label: "Critical" },
  unknown:   { primary: "#6B7491", ring: "#9BA4B8", bgSoft: "#F4F6FB", ink: "#4B5570", label: "Loading" },
};

/** Circular SVG progress ring used in the hero. */
function ScoreRing({ score, color, ariaLabel }) {
  const radius = 62;
  const stroke = 10;
  const normalized = radius - stroke / 2;
  const circumference = 2 * Math.PI * normalized;
  const pct = Math.max(0, Math.min(100, score || 0)) / 100;
  const offset = circumference * (1 - pct);
  return (
    <svg
      width={148}
      height={148}
      viewBox="0 0 148 148"
      role="img"
      aria-label={ariaLabel}
      data-testid="health-score-ring"
    >
      <circle
        cx={74}
        cy={74}
        r={normalized}
        stroke="#E8EAF0"
        strokeWidth={stroke}
        fill="none"
      />
      <circle
        cx={74}
        cy={74}
        r={normalized}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform="rotate(-90 74 74)"
        style={{ transition: "stroke-dashoffset 1s ease-out, stroke 0.3s ease-out" }}
      />
    </svg>
  );
}

function DriverBar({ driver, palette }) {
  const pct = (driver.contribution / driver.max_contribution) * 100;
  const displayValue = (() => {
    if (driver.value === null || driver.value === undefined) return "--";
    if (driver.unit === "percent") return `${Number(driver.value).toFixed(1)}%`;
    if (driver.unit === "ratio") return `${Number(driver.value).toFixed(2)}x`;
    if (driver.unit === "months") return `${Number(driver.value).toFixed(1)} mo`;
    return String(driver.value);
  })();
  return (
    <div
      className="rounded-lg p-3"
      style={{ background: "#FFFFFF", border: "1px solid #E8EAF0" }}
      data-testid={`health-driver-${driver.key}`}
    >
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="text-[11px] font-semibold" style={{ color: "#1C2040" }}>{driver.label}</span>
        <span className="text-[10px] font-mono" style={{ color: palette.ink }}>
          {Math.round(driver.contribution)}/{driver.max_contribution}
        </span>
      </div>
      <div className="flex items-baseline gap-2 mb-1.5">
        <span className="text-base font-bold" style={{ color: palette.primary }} data-testid={`health-driver-${driver.key}-value`}>{displayValue}</span>
        <span className="text-[10px]" style={{ color: "#6B7491" }}>{driver.sub_label}</span>
      </div>
      <div
        className="h-1.5 rounded-full overflow-hidden"
        style={{ background: "#E8EAF0" }}
        aria-label={`${driver.label} contribution ${Math.round(driver.contribution)} of ${driver.max_contribution}`}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.min(100, Math.max(0, pct))}%`,
            background: palette.primary,
            transition: "width 0.8s ease-out",
          }}
        />
      </div>
      <p className="text-[10px] mt-1.5 leading-snug" style={{ color: "#4B5570" }}>{driver.insight}</p>
    </div>
  );
}

export function HealthScoreHero() {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get("/metrics/health-score/detail");
        if (!cancelled) setDetail(res.data);
      } catch {
        if (!cancelled) setError(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const palette = detail ? (TIER_COLORS[detail.tier] || TIER_COLORS.unknown) : TIER_COLORS.unknown;
  const score = detail?.score ?? 0;
  const tooltip = METRIC_TOOLTIPS.health_score;

  return (
    <div
      data-testid="health-score-hero"
      className="rounded-2xl p-4 sm:p-5"
      style={{
        background: `linear-gradient(135deg, #FFFFFF 0%, ${palette.bgSoft} 100%)`,
        border: `1px solid ${palette.primary}33`,
        boxShadow: `0 4px 20px ${palette.primary}18`,
      }}
      role="region"
      aria-label="Business Health Score"
    >
      <div className="flex flex-col md:flex-row items-stretch gap-4">
        {/* Left: Score + Tier */}
        <div className="flex items-center gap-4 md:min-w-[280px]">
          <div className="relative flex-shrink-0">
            <ScoreRing score={score} color={palette.ring} ariaLabel={`Health score ${score} out of 100`} />
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-3xl font-black leading-none" style={{ color: palette.primary }} data-testid="health-score-value">
                {detail ? Math.round(score) : "--"}
              </span>
              <span className="text-[10px] font-semibold tracking-wider" style={{ color: "#6B7491" }}>OUT OF 100</span>
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <Sparkles size={14} style={{ color: palette.primary }} />
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#6B7491" }}>
                Business Health Score
              </span>
              {tooltip && (
                <MetricTooltip
                  type="info"
                  content={tooltip.info}
                  metricKey="health_score"
                  testKey="health-score-hero"
                />
              )}
            </div>
            <div className="flex items-baseline gap-2 mb-1.5">
              <span
                className="text-2xl font-black leading-tight"
                style={{ color: palette.primary }}
                data-testid="health-score-tier"
              >
                {detail?.tier_label || "Loading"}
              </span>
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: palette.primary, color: "#FFFFFF" }}
                data-testid="health-score-badge"
              >
                LIVE
              </span>
            </div>
            <p className="text-[11px] leading-snug max-w-xs" style={{ color: "#4B5570" }}>
              {detail?.guidance?.[detail?.tier] || "Loading composite score from live financial data..."}
            </p>
            {tooltip && (
              <div className="flex items-center gap-1 mt-1.5 text-[10px] font-semibold" style={{ color: palette.ink }}>
                <ArrowUpRight size={11} />
                <MetricTooltip
                  type="help"
                  content={tooltip.help}
                  testKey="health-score-hero"
                />
                <span className="ml-1">Rule of thumb</span>
              </div>
            )}
            {/* Live Cash Runway chip (QuickBooks-powered when connected). */}
            <div className="mt-2">
              <CashRunwayChip />
            </div>
          </div>
        </div>

        {/* Right: Driver breakdown */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#6B7491" }}>
              Composite Drivers (25 pts each)
            </span>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: palette.bgSoft, color: palette.ink }}>
              API
            </span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {error && (
              <div className="col-span-full text-[11px] p-3 rounded-lg" style={{ color: "#4B5570", background: "#F4F6FB" }} data-testid="health-score-error">
                Health score temporarily unavailable. Retry from the dashboard reload.
              </div>
            )}
            {!error && !detail && [0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-lg p-3 animate-pulse" style={{ background: "#F4F6FB", border: "1px solid #E8EAF0", minHeight: 92 }} data-testid={`health-driver-skeleton-${i}`} />
            ))}
            {detail?.drivers?.map((d) => (
              <DriverBar key={d.key} driver={d} palette={palette} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HealthScoreHero;
